import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@mui/material";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { off, onValue, ref } from "firebase/database";
import { useParams } from "react-router-dom";

import { database } from "../../firebase";
import MetricCard from "../ui/MetricCard";
import RecordDialog from "../ui/RecordDialog";
import EmptyState from "../ui/EmptyState";
import Chart from "../charts/Chart";
import {
  baseChartOption,
  chartInk,
  lineSeries,
  metricColor,
  withAlpha,
} from "../charts/chartTheme";
import {
  EM_DASH,
  describe,
  formatRelative,
  formatTime,
  formatTimestamp,
  latestOf,
  monthlyAverage,
} from "../../utils/format";

/* Clinical reference band. Drawn behind the trace so a reading can be judged
   without reading the axis. */
const NORMAL_FLOOR = 95;

const SPO2 = ({ title = "Blood oxygen", extraActions, menu }) => {
  const { watchId } = useParams();
  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const spo2Ref = ref(database, `Health/${watchId}/spo2`);

    const handler = onValue(spo2Ref, (snapshot) => {
      if (!snapshot.exists()) {
        setRecords([]);
        setSelectedRecord(null);
        return;
      }

      const parsed = Object.values(snapshot.val())
        .filter((entry) => entry?.spo2Status === 1 && entry?.spo2 > 0 && entry?.url)
        .map((entry) => ({ ...entry, timestamp: Number(entry.timestamp) }))
        .sort((a, b) => b.timestamp - a.timestamp);

      setRecords(parsed);
      setSelectedRecord((previous) => {
        if (!previous) return parsed[0] ?? null;
        return (
          parsed.find((record) => record.timestamp === previous.timestamp) ??
          parsed[0] ??
          null
        );
      });
    });

    return () => off(spo2Ref, "value", handler);
  }, [watchId]);

  const series = useMemo(
    () => records.slice().sort((a, b) => a.timestamp - b.timestamp),
    [records]
  );
  const values = useMemo(() => series.map((r) => Number(r.spo2)), [series]);
  const stats = useMemo(() => describe(values, 0), [values]);

  const accent = metricColor("spo2");
  const selectedIndex = selectedRecord
    ? series.findIndex((r) => r.timestamp === selectedRecord.timestamp)
    : -1;

  const belowNormal = values.filter((v) => v < NORMAL_FLOOR).length;

  const buildOption = () => {
    const color = metricColor("spo2");
    const line = lineSeries({
      name: "SpO2",
      data: values,
      color,
      showSymbol: values.length <= 40,
    });

    line.markLine = {
      silent: true,
      symbol: "none",
      label: { show: false },
      data: [
        {
          yAxis: NORMAL_FLOOR,
          lineStyle: { color: withAlpha("#fab219", 0.9), type: [5, 5], width: 1.5 },
          label: {
            show: true,
            formatter: `Normal ≥ ${NORMAL_FLOOR}%`,
            position: "insideEndTop",
            color: chartInk().label,
            fontSize: 11,
          },
        },
        // Vertical marker for the selected reading — same line, no label.
        ...(selectedIndex >= 0
          ? [
              {
                xAxis: selectedIndex,
                lineStyle: { color: withAlpha(color, 0.4), type: [4, 4], width: 1 },
              },
            ]
          : []),
      ],
    };

    if (selectedIndex >= 0) {
      line.markPoint = {
        symbol: "circle",
        symbolSize: 11,
        itemStyle: { color, borderColor: "#fff", borderWidth: 2 },
        label: { show: false },
        data: [{ coord: [selectedIndex, values[selectedIndex]] }],
      };
    }

    return baseChartOption({
      categories: series.map((r) => formatTime(r.timestamp)),
      series: [line],
      yName: "%",
      yMin: 80,
      yMax: 100,
      tooltipFormatter: (params) => {
        const point = params?.[0];
        if (!point) return "";
        return `${point.axisValue}<br/><strong>${point.value}% SpO2</strong>`;
      },
    });
  };

  return (
    <>
      <MetricCard
        accent={accent}
        icon={<WaterDropOutlinedIcon />}
        title={title}
        meta={
          <>
            <span className="wg-chip wg-chip--accent">
              {selectedRecord ? "Record loaded" : "No records"}
            </span>
            {selectedRecord && (
              <span className="wg-chip">
                {formatTimestamp(selectedRecord.timestamp)}
              </span>
            )}
            {belowNormal > 0 && (
              <span className="wg-chip wg-chip--warning">
                {belowNormal} below {NORMAL_FLOOR}%
              </span>
            )}
          </>
        }
        menu={menu}
        actions={
          <>
            {extraActions}
            <Button
              size="small"
              startIcon={<HistoryOutlinedIcon />}
              onClick={() => setSelectedRecord(latestOf(records))}
              disabled={!records.length}
            >
              Latest
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setDialogOpen(true)}
              disabled={!records.length}
            >
              Select record
            </Button>
          </>
        }
        note={selectedRecord?.spo2StatusDescription}
        stats={[
          {
            label: "Selected",
            value: selectedRecord?.spo2 ?? EM_DASH,
            unit: "%",
            meta: selectedRecord?.displayName || "Unassigned",
          },
          { label: "Average", value: stats.avg, unit: "%", meta: `${stats.count} samples` },
          { label: "Range", value: stats.range, unit: "%", meta: "Observed window" },
          {
            label: "This month",
            value: monthlyAverage(records, (r) => r.spo2, 0),
            unit: "%",
            meta: `${records.length} records`,
          },
        ]}
        empty={
          !series.length ? (
            <EmptyState
              icon={<WaterDropOutlinedIcon />}
              title="No blood oxygen data yet"
              copy="Completed SpO2 measurements appear here with the normal-range reference band."
            />
          ) : null
        }
      >
        <Chart buildOption={buildOption} height={300} deps={[values, selectedIndex]} />
      </MetricCard>

      <RecordDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSelect={(record) => {
          setSelectedRecord(record);
          setDialogOpen(false);
        }}
        records={records}
        accent={accent}
        title="Blood oxygen records"
        getTitle={(record) => `${record.spo2}%`}
        getMeta={(record) =>
          `${formatTimestamp(record.timestamp)} · ${formatRelative(record.timestamp)}`
        }
        isSelected={(record) => selectedRecord?.timestamp === record.timestamp}
      />
    </>
  );
};

export default SPO2;
